import { createContext, useState, useEffect, useRef, ReactNode } from "react";
import { User, LoginCredentials, RegisterData } from "../types";
import { queryClient } from "../lib/queryClient";
import { supabase } from "../lib/supabase";
import type { Session, User as AuthUser } from "@supabase/supabase-js";
import { appFetch, resolveApiUrl } from "../lib/runtime";

async function ensureUserProfile(authUser: AuthUser): Promise<User | null> {
  try {
    const { data, error: selectError } = await supabase
      .from("users")
      .select("id, username, email, created_at")
      .eq("id", authUser.id)
      .single();

    if (selectError && selectError.code !== "PGRST116") {
      console.error("Error checking if user exists:", selectError.message);
      return null;
    }

    if (!data) {
      const userData = {
        id: authUser.id,
        username:
          authUser.user_metadata?.username ||
          authUser.email?.split("@")[0] ||
          "",
        email: authUser.email,
        created_at: new Date().toISOString(),
      };

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        console.error("No token available to create user profile");
        return null;
      }

      const response = await appFetch(resolveApiUrl("/api/user"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Error creating user profile:", errorData);
        return null;
      }

      return userData as User;
    }

    return data as User;
  } catch (error) {
    console.error("Error ensuring user profile:", error);
    return null;
  }
}

function fallbackUser(authUser: AuthUser): User {
  return {
    id: String(authUser.id),
    username:
      authUser.user_metadata?.username ||
      authUser.email?.split("@")[0] ||
      "",
    email: authUser.email || "",
    created_at: new Date().toISOString(),
  };
}

interface AuthContextProps {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

export const AuthContext = createContext<AuthContextProps>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  error: null,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  /** True only until the first session check finishes — not for token refreshes. */
  const [isInitializing, setIsInitializing] = useState(true);
  /** True during explicit login/register/logout actions. */
  const [isAuthActionPending, setIsAuthActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  const applySession = async (session: Session | null, opts?: { ensureProfile?: boolean }) => {
    if (!session?.user) {
      setUser(null);
      userIdRef.current = null;
      return;
    }

    const authUser = session.user;

    // Same user already loaded — skip profile round-trip (token refresh path)
    if (opts?.ensureProfile === false && userIdRef.current === authUser.id) {
      return;
    }

    const profile = await ensureUserProfile(authUser);
    const next = profile || fallbackUser(authUser);
    userIdRef.current = next.id;
    setUser(next);
  };

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    const initializeAuth = async () => {
      setIsInitializing(true);
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;
        await applySession(session, { ensureProfile: true });
      } catch (err) {
        console.error("Auth initialization error:", err);
        setUser(null);
        userIdRef.current = null;
      } finally {
        setIsInitializing(false);
      }

      const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
        // TOKEN_REFRESHED fires when returning to a backgrounded tab.
        // Never flip global loading for that — it unmounts protected pages.
        if (event === "TOKEN_REFRESHED") {
          if (!session) {
            setUser(null);
            userIdRef.current = null;
          } else if (userIdRef.current !== session.user.id) {
            await applySession(session, { ensureProfile: true });
          }
          return;
        }

        if (event === "SIGNED_IN" || event === "USER_UPDATED") {
          await applySession(session, { ensureProfile: true });
          return;
        }

        if (event === "SIGNED_OUT") {
          setUser(null);
          userIdRef.current = null;
          queryClient.clear();
        }
      });

      subscription = data.subscription;
    };

    void initializeAuth();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setError(null);
    setIsAuthActionPending(true);
    try {
      const email = credentials.email || credentials.username;
      if (!email || !email.includes("@")) {
        throw new Error("Please sign in with your email address");
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: credentials.password,
      });

      if (signInError) throw new Error(signInError.message);
      if (!data.user) throw new Error("No user returned from login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsAuthActionPending(false);
    }
  };

  const register = async (data: RegisterData) => {
    setError(null);
    setIsAuthActionPending(true);
    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            username: data.username,
          },
        },
      });

      if (signUpError) throw new Error(signUpError.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsAuthActionPending(false);
    }
  };

  const logout = async () => {
    setIsAuthActionPending(true);
    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;
      setUser(null);
      userIdRef.current = null;
      queryClient.clear();
    } catch (err) {
      console.error("Error during logout:", err);
    } finally {
      setIsAuthActionPending(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        // Protected routes should only block on first load; action pending is for forms
        isLoading: isInitializing || (isAuthActionPending && !user),
        isAuthenticated: !!user,
        login,
        register,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
